/**
 * GA4 / GTM eCommerce dataLayer utilities.
 *
 * All events follow the GA4 Enhanced Ecommerce spec so they work
 * whether GA4 is loaded directly OR via a GTM container tag.
 *
 * Reference:
 *  https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** Raw product shape expected by GTM/GA4. */
export interface GA4Item {
  item_id: string;
  item_name: string;
  affiliation?: string;
  coupon?: string;
  currency?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

function push(event: string, payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // GA4 spec: clear previous ecommerce object before each event
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ...payload });
}

/** Map a product model to a GA4 item object. */
export function toGA4Item(
  product: { productID: number; productName: string; unitCost?: number; domainCategory?: number },
  overrides: Partial<GA4Item> = {},
): GA4Item {
  return {
    item_id: String(product.productID),
    item_name: product.productName ?? '',
    affiliation: 'CarSparesEzyFind',
    currency: 'ZAR',
    price: product.unitCost ?? 0,
    item_brand: 'CarSparesEzyFind',
    item_category: overrides.item_category ?? 'Car Parts',
    quantity: 1,
    ...overrides,
  };
}

// ─────────────────────────────────────────────
// Standard GA4 eCommerce events
// ─────────────────────────────────────────────

/**
 * Fired when a list of products is shown to the user.
 * GA4 event: `view_item_list`
 */
export function trackViewItemList(
  products: { productID: number; productName: string; unitCost?: number; domainCategory?: number }[],
  listId = 'car_parts_list',
  listName = 'Car Parts & Spares',
) {
  const items = products.map((p, i) =>
    toGA4Item(p, { index: i, item_list_id: listId, item_list_name: listName }),
  );
  push('view_item_list', {
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items,
    },
  });
}

/**
 * Fired when the user clicks a product in a list.
 * GA4 event: `select_item`
 */
export function trackSelectItem(
  product: { productID: number; productName: string; unitCost?: number; domainCategory?: number },
  index: number,
  listId = 'car_parts_list',
  listName = 'Car Parts & Spares',
) {
  push('select_item', {
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: [toGA4Item(product, { index, item_list_id: listId, item_list_name: listName })],
    },
  });
}

/**
 * Fired when the user views a product detail page.
 * GA4 event: `view_item`
 */
export function trackViewItem(
  product: { productID: number; productName: string; unitCost?: number; domainCategory?: number },
) {
  push('view_item', {
    ecommerce: {
      currency: 'ZAR',
      value: product.unitCost ?? 0,
      items: [toGA4Item(product)],
    },
  });
}

/**
 * Fired when the user adds a product to cart.
 * GA4 event: `add_to_cart`
 */
export function trackAddToCart(
  product: { productID: number; productName: string; unitCost?: number; domainCategory?: number },
  quantity = 1,
) {
  push('add_to_cart', {
    ecommerce: {
      currency: 'ZAR',
      value: (product.unitCost ?? 0) * quantity,
      items: [toGA4Item(product, { quantity })],
    },
  });
}

/**
 * Fired when the user initiates checkout.
 * GA4 event: `begin_checkout`
 */
export function trackBeginCheckout(
  product: { productID: number; productName: string; unitCost?: number; domainCategory?: number },
  quantity = 1,
) {
  push('begin_checkout', {
    ecommerce: {
      currency: 'ZAR',
      value: (product.unitCost ?? 0) * quantity,
      items: [toGA4Item(product, { quantity })],
    },
  });
}

/**
 * Fired when a category is selected / filter applied.
 * Custom event consumed by GTM triggers.
 */
export function trackCategorySelect(categoryId: string | number, categoryName: string) {
  push('category_select', {
    category_id: String(categoryId),
    category_name: categoryName,
  });
}
